/**
 * Wrapper for d3
 * Node Package @link https://www.npmjs.com/package/d3
 * API @link https://github.com/d3/d3/blob/master/API.md
 */
import { zoom, zoomTransform, zoomIdentity } from "d3-zoom";
import { select } from "d3-selection";
const duration = 500;

const d3Plugin = {
  install(app) {
    /**
     * Plugin API
     */
    app.config.globalProperties.$d3 = {
      /**
       * @summary Returns the d3Zoom object for zooming
       * @param {Object} params - The parameters containing the configurations for d3Zoom
       * @param {Function} handler - The function that will be binded to the zoom event
       * @example
       * this.$d3.zoom({scaleExtent: [1,2]}, () => { logger.debug('Zoomed.')});
       */
      zoom(params, handler) {
        let d3ZoomObject = zoom();

        // scaleExtent: https://www.npmjs.com/package/d3-zoom#zoom_scaleExtent
        if (params.scaleExtent) {
          d3ZoomObject.scaleExtent(params.scaleExtent);
        }
        // translateExtent: https://www.npmjs.com/package/d3-zoom#zoom_translateExtent
        if (params.translateExtent) {
          d3ZoomObject.translateExtent(params.translateExtent);
        }
        // zoomExtent: https://www.npmjs.com/package/d3-zoom#zoom_extent
        if (params.zoomExtent) {
          d3ZoomObject.extent(params.zoomExtent);
        }
        d3ZoomObject.on("zoom", handler.bind(null));

        return d3ZoomObject;
      },
      /**
       * @summary Returns the transform matrix for the selected element
       * @param {String} selector - The selector of the element to query
       * @example
       * this.$d3.zoomTransform(".image-wrapper");
       * @returns {Object} - {
       *  k: number - the scale factor
       *  x: number - the translation number in the x-plane
       *  y: number - the translation number in the y-plane
       * }
       */
      zoomTransform(selector) {
        return zoomTransform(selector);
      },
      /**
       * @summary Returns the selection element to be used for d3 events
       * @param {String} selector - The selector of the element to query.
       * @example
       * this.$d3.select(".image-wrapper");
       */
      select(selector) {
        return select(selector);
      },
      /**
       * @summary Scale the selected element to the factor determined by the argument passed in
       * @param {Object} d3ZoomObject - The d3-zoom object
       * @param {Object} selector - The d3-selection element
       * @param {Number} factor - The scale factor to scale by
       * @example
       * this.$d3.scaleTo(d3Zoom, imageWrapper, 2)
       */
      scaleTo(d3ZoomObject, selector, factor = 1) {
        selector.transition().duration(duration).call(d3ZoomObject.scaleTo, factor);
      },
      /**
       * @summary Reset the zoom transform to the zoom identity of {x=0, y=0, k=1}
       * @param {Object} d3ZoomObject - The d3-zoom object
       * @param {Object} selector - The d3-selection element
       * @example
       * this.$d3.reset(d3Zoom, imageWrapper)
       */
      reset(d3ZoomObject, selector) {
        selector.transition().duration(duration).call(d3ZoomObject.transform, zoomIdentity);
      },
    };
  },
};
export default d3Plugin;
